import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  aiGenerateDetails,
  AIGenerateDetailsResponse,
  Persona,
  updatePersonaValues,
} from "@/api/personas.ts";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import set from "lodash/set";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface QuickCreatePersonaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (persona: Persona) => void;
  personaId?: string;
  personaName?: string;
  currentPersonaData?: Persona;
}

type ModalView = "select" | "loading" | "results" | "error";

const GENERATABLE_FIELDS = {
  basic: [
    { id: "short_bio", label: "Short Bio" },
    { id: "persona", label: "AI Personality" },
    { id: "interests", label: "Interests" },
  ],
  appearance: [
    { id: "appearance.age", label: "Age" },
    { id: "appearance.gender", label: "Gender" },
    { id: "appearance.hair_color", label: "Hair Color" },
    { id: "appearance.eye_color", label: "Eye Color" },
    { id: "appearance.skin_tone", label: "Skin Tone" },
    { id: "appearance.body_type", label: "Body Type" },
    { id: "appearance.fashion", label: "Fashion" },
    { id: "appearance.height", label: "Height" },
    { id: "appearance.weight", label: "Weight" },
    { id: "appearance.ethnicity", label: "Ethnicity" },
  ],
  additional_background: [
    { id: "bio", label: "Bio" },
    { id: "occupation", label: "Occupation" },
    { id: "style", label: "Style" },
    { id: "target_audience", label: "Target Audience" },
    { id: "persona_objective", label: "Agent Objective" },
  ],
};

const isFieldEmpty = (
  persona: Persona | undefined,
  fieldId: string,
): boolean => {
  if (!persona) return true;
  const value = get(persona, fieldId);

  return isEmpty(value) || value === null || value === undefined;
};

export const QuickCreatePersonaModal: React.FC<
  QuickCreatePersonaModalProps
> = ({
  isOpen,
  onClose,
  onSuccess,
  personaId,
  personaName,
  currentPersonaData,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [view, setView] = useState<ModalView>("select");
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  const [generatedPersona, setGeneratedPersona] = useState<Persona | null>(
    null,
  );
  const [feedback, setFeedback] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen && currentPersonaData) {
      const initiallySelected = new Set<string>();
      Object.values(GENERATABLE_FIELDS)
        .flat()
        .forEach((field) => {
          if (isFieldEmpty(currentPersonaData, field.id)) {
            initiallySelected.add(field.id);
          }
        });
      setSelectedFields(initiallySelected);
    } else if (!isOpen) {
      setFeedback("");
    }
  }, [isOpen, currentPersonaData]);

  const mutation = useMutation({
    mutationFn: aiGenerateDetails,
    onMutate: () => {
      setGeneratedPersona(null);
    },
    onSuccess: (data: AIGenerateDetailsResponse) => {
      setGeneratedPersona(data.persona);
      setView("results");

      // Only invalidate cache and call parent onSuccess if NOT in preview mode
      // Check if this was a save operation (no preview_only flag)
      const isSaveOperation = !data.message.includes("not saved");

      if (isSaveOperation) {
        // This was a real save operation, not a preview
        queryClient.invalidateQueries({ queryKey: ["personas", personaId] });
        queryClient.invalidateQueries({ queryKey: ["personas"] });
        onSuccess(data.persona);
        toast({
          title: "AI Quick Create Successful",
          description: "Agent details generated and saved.",
        });
        setLoading(false);
      } else {
        // Just a preview, don't call onSuccess which would close the modal
        toast({
          title: "AI Generation Complete",
          description: "Review the generated details before saving.",
        });
      }
    },
    onError: (error: any) => {
      console.error("AI Generation error:", error);
      toast({
        variant: "destructive",
        title: "AI Quick Create Error",
        description:
          error.response?.data?.detail?.error ||
          error.message ||
          "An unexpected error occurred.",
      });
      // Explicitly set view to error and clear any pending state
      setView("error");
    },
    // Add retry logic to avoid silent failures
    retry: false,
  });

  const handleFieldSelectionChange = (fieldId: string, checked: boolean) => {
    setSelectedFields((prev) => {
      const next = new Set(prev);
      if (checked) next.add(fieldId);
      else next.delete(fieldId);
      return next;
    });
  };

  const handleSelectAllSection = (
    section: keyof typeof GENERATABLE_FIELDS,
    checked: boolean,
  ) => {
    setSelectedFields((prev) => {
      const next = new Set(prev);
      GENERATABLE_FIELDS[section].forEach((field) => {
        if (checked) {
          next.add(field.id);
        } else {
          next.delete(field.id);
        }
      });
      return next;
    });
  };

  const triggerGeneration = () => {
    if (!personaId) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Cannot populate agent details without an ID.",
      });
      return;
    }
    if (selectedFields.size === 0) {
      toast({
        variant: "destructive",
        title: "No Fields Selected",
        description: "Please select at least one field to generate.",
      });
      return;
    }

    // Set view to loading immediately to give feedback
    setView("loading");

    // Create a separate try/catch to ensure errors are properly handled
    try {
      mutation.mutate(
        {
          persona_id: personaId,
          fields_to_generate: Array.from(selectedFields),
          feedback: feedback || undefined,
          preview_only: true,
        },
        {
          onError: (error: any) => {
            toast({
              variant: "destructive",
              title: "AI Quick Create Error",
              description:
                error.response?.data?.detail?.error ||
                error.message ||
                "An unexpected error occurred.",
            });
            // Make sure to reset view to select on error
            setView("error");
          },
        },
      );
    } catch (error: any) {
      // Fallback error handling in case mutation throws
      toast({
        variant: "destructive",
        title: "AI Quick Create Error",
        description: error.message || "An unexpected error occurred.",
      });
      setView("select"); // Reset to selection view on error
    }
  };

  const handleClose = () => {
    setView("select");
    setGeneratedPersona(null);
    setSelectedFields(new Set());
    setFeedback("");
    onClose();
  };

  const handleSaveChanges = () => {
    if (!personaId || !generatedPersona) return;

    setLoading(true);
    toast({
      title: "Saving changes...",
      description: "Your agent details are being saved.",
    });

    // Create a direct save function that doesn't call Claude again
    const saveChanges = async () => {
      try {
        // Extract the generated values we want to save
        const updates: Record<string, any> = {};

        // Iterate through the fields selected for generation
        Array.from(selectedFields).forEach((fieldId) => {
          // Get the generated value using the dot-notation path from the nested generatedPersona state
          const value = get(generatedPersona, fieldId);

          // Only include the field in the update if a non-null/undefined value was generated
          if (value !== undefined && value !== null) {
            // Use lodash.set to populate the updates object using the dot path.
            // This correctly creates nested structures as needed.
            set(updates, fieldId, value);

            // --- Refined Age/Gender Synchronization ---
            // If we updated appearance.age or appearance.gender, AND the top-level field was also selected,
            // ensure the top-level field is included in the update payload.
            if (fieldId.startsWith("appearance.")) {
              const baseField = fieldId.split(".")[1];
              if (
                (baseField === "age" || baseField === "gender") &&
                selectedFields.has(baseField)
              ) {
                // Use set again to ensure the top-level field exists
                set(updates, baseField, value);
              }
            }
            // If we updated top-level age or gender, AND the corresponding appearance field was also selected,
            // ensure the nested appearance field is included in the update payload.
            else if (
              (fieldId === "age" || fieldId === "gender") &&
              selectedFields.has(`appearance.${fieldId}`)
            ) {
              // Use set again to ensure the nested field exists
              set(updates, `appearance.${fieldId}`, value);
            }
          }
        });

        // Check if any updates were actually prepared
        if (isEmpty(updates)) {
          toast({
            title: "No Changes Detected",
            description:
              "No new values were generated for the selected fields.",
          });
          setLoading(false);
          return; // Don't call API if no updates
        }

        console.log("Saving updates:", updates); // Use console.log for browser logging

        // Save the updates directly using the API endpoint
        await updatePersonaValues(personaId, updates);

        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ["personas", personaId] });
        queryClient.invalidateQueries({ queryKey: ["personas"] });

        toast({
          title: "Changes Saved Successfully",
          description: "Your agent details have been saved.",
        });

        // Call the parent's success handler *before* resetting internal state
        onSuccess(generatedPersona);

        // Now, explicitly reset the modal's internal state by calling handleClose
        // This also handles setLoading(false) implicitly if needed and calls parent's onClose
        handleClose();
      } catch (error: any) {
        toast({
          variant: "destructive",
          title: "Save Failed",
          description:
            error.response?.data?.detail || "Failed to save the changes.",
        });
        setLoading(false);
      }
    };

    saveChanges();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-6xl">
        {view === "select" && (
          <>
            <DialogHeader>
              <DialogTitle>
                AI Quick Create - Select Agent Attributes
              </DialogTitle>
              <DialogDescription>
                Select which attributes you'd like the AI to generate for
                {personaName ? ` "${personaName}"` : " this agent"}. Our AI will
                analyze the agent's existing data and image to create cohesive,
                authentic details.
              </DialogDescription>
            </DialogHeader>
            {!personaId ? (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>
                  No agent selected. Please close this and try again.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="py-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6">
                  {Object.entries(GENERATABLE_FIELDS).map(([key, fields]) => {
                    const sectionKey = key as keyof typeof GENERATABLE_FIELDS;
                    const allInSectionSelected = fields.every((f) =>
                      selectedFields.has(f.id),
                    );

                    return (
                      <div key={sectionKey} className="flex flex-col space-y-3">
                        <div className="flex items-center justify-between mb-1 border-b border-gray-700 pb-2">
                          <h3 className="font-semibold text-base text-[#c8ff00]">
                            {sectionKey
                              .replace("_", " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                          </h3>
                          <div className="flex items-center space-x-2">
                            <Label
                              htmlFor={`select-all-${sectionKey}`}
                              className="text-xs text-gray-400"
                            >
                              Select All
                            </Label>
                            <Checkbox
                              id={`select-all-${sectionKey}`}
                              checked={allInSectionSelected}
                              onCheckedChange={(checked) =>
                                handleSelectAllSection(sectionKey, !!checked)
                              }
                            />
                          </div>
                        </div>
                        <div className="space-y-4 pl-2">
                          {fields.map((field) => {
                            const currentValue = get(
                              currentPersonaData,
                              field.id,
                            );
                            const isChecked = selectedFields.has(field.id);
                            const displayValue = Array.isArray(currentValue)
                              ? currentValue.join(", ")
                              : String(currentValue ?? "");

                            const useTextarea = [
                              "bio",
                              "short_bio",
                              "persona",
                              "style",
                              "persona_objective",
                              "interests",
                              "target_audience",
                            ].includes(field.id);

                            let defaultRows = 1;
                            if (
                              ["bio", "persona", "style"].includes(field.id)
                            ) {
                              defaultRows = 4;
                            } else if (
                              [
                                "short_bio",
                                "persona_objective",
                                "interests",
                                "target_audience",
                              ].includes(field.id)
                            ) {
                              defaultRows = 2;
                            }
                            if (
                              useTextarea &&
                              displayValue.length > 100 &&
                              defaultRows < 4
                            ) {
                              defaultRows = 4;
                            } else if (
                              useTextarea &&
                              displayValue.length > 50 &&
                              defaultRows < 3
                            ) {
                              defaultRows = 3;
                            } else if (
                              useTextarea &&
                              displayValue.length > 20 &&
                              defaultRows < 2
                            ) {
                              defaultRows = 2;
                            }

                            return (
                              <div key={field.id} className="space-y-1">
                                <div className="flex items-center space-x-2">
                                  <Checkbox
                                    id={field.id}
                                    checked={isChecked}
                                    onCheckedChange={(checked) =>
                                      handleFieldSelectionChange(
                                        field.id,
                                        !!checked,
                                      )
                                    }
                                    className="flex-shrink-0"
                                  />
                                  <Label
                                    htmlFor={field.id}
                                    className="text-sm font-medium leading-none flex-shrink-0"
                                  >
                                    {field.label}
                                  </Label>
                                </div>
                                {useTextarea ? (
                                  <Textarea
                                    defaultValue={displayValue}
                                    className="text-xs text-gray-300 bg-gray-800/50 border-gray-700 h-auto min-h-[40px]"
                                    rows={defaultRows}
                                  />
                                ) : (
                                  <Input
                                    defaultValue={displayValue}
                                    className="text-xs text-gray-300 bg-gray-800/50 border-gray-700 h-8"
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-8 pt-6 border-t border-gray-700 space-y-2">
                  <Label htmlFor="ai-feedback">Optional Guidance for AI</Label>
                  <Textarea
                    id="ai-feedback"
                    placeholder='e.g., "Make the bio sound more professional" or "Focus interests on blockchain and Web3" or "Style should be casual and approachable"'
                    value={feedback}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setFeedback(e.target.value)
                    }
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">
                    Your guidance helps our AI create more personalized and
                    relevant content for your agent.
                  </p>
                </div>
              </div>
            )}
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={triggerGeneration}
                disabled={
                  !personaId || selectedFields.size === 0 || mutation.isPending
                }
                className="bg-[#c8ff00] text-black hover:bg-[#a8df00]"
              >
                {mutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Generate {selectedFields.size} Attributes with AI
              </Button>
            </DialogFooter>
          </>
        )}

        {view === "loading" && (
          <div className="flex flex-col items-center justify-center p-8 gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-[#c8ff00]" />
            <p className="text-white">Creating authentic agent details...</p>
            <p className="text-sm text-gray-400">
              Our AI is crafting unique attributes for your agent based on your
              selections.
            </p>
            <p className="text-xs text-gray-500">
              This typically takes 5-15 seconds.
            </p>
          </div>
        )}

        {view === "error" && (
          <>
            <DialogHeader>
              <DialogTitle className="text-red-500">
                Generation Didn't Work
              </DialogTitle>
              <DialogDescription>
                We encountered an issue while generating your agent's details.
                You can try again with the same selections or modify them.
              </DialogDescription>
            </DialogHeader>
            <Alert variant="destructive" className="my-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>What happened</AlertTitle>
              <AlertDescription>
                {mutation.error?.message ||
                  "Our AI had trouble creating content for your agent. This sometimes happens when requesting many attributes at once."}
              </AlertDescription>
            </Alert>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setView("select")}
              >
                Modify Selections
              </Button>
              <Button
                type="button"
                onClick={() => {
                  // Clear any error state before retrying
                  setView("select");
                  // Wait a moment for state to update before triggering generation
                  setTimeout(() => {
                    triggerGeneration();
                  }, 100);
                }}
                disabled={!personaId || selectedFields.size === 0}
                className="bg-[#c8ff00] text-black hover:bg-[#a8df00]"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </DialogFooter>
          </>
        )}

        {view === "results" && generatedPersona && (
          <>
            <DialogHeader>
              <DialogTitle>Review Your AI-Generated Content</DialogTitle>
              <DialogDescription>
                Here's what our AI created for your agent. You can preview
                everything before saving. Click{" "}
                <span className="font-bold text-[#c8ff00]">Save Changes</span>{" "}
                to apply these details to your agent or go back to modify your
                selections.
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh] p-1 pr-4 mt-4">
              <div className="space-y-4 py-4 bg-gray-900/30 p-4 rounded-md">
                {Object.keys(GENERATABLE_FIELDS).map((sectionKey) => {
                  let sectionHasData = false;
                  const sectionRenderedFields = GENERATABLE_FIELDS[
                    sectionKey as keyof typeof GENERATABLE_FIELDS
                  ]
                    .map((field) => {
                      const value = get(generatedPersona, field.id);
                      if (!isFieldEmpty(generatedPersona, field.id)) {
                        sectionHasData = true;
                        const displayValue = Array.isArray(value)
                          ? value.join(", ")
                          : String(value ?? "");
                        const isTextareaField = [
                          "bio",
                          "short_bio",
                          "persona",
                          "style",
                          "persona_objective",
                          "interests",
                          "target_audience",
                        ].includes(field.id);

                        let resultRows = 1;
                        if (["bio", "persona", "style"].includes(field.id)) {
                          resultRows = 4;
                        } else if (
                          [
                            "short_bio",
                            "persona_objective",
                            "interests",
                            "target_audience",
                          ].includes(field.id)
                        ) {
                          resultRows = 2;
                        }
                        if (
                          isTextareaField &&
                          displayValue.length > 100 &&
                          resultRows < 4
                        ) {
                          resultRows = 4;
                        } else if (
                          isTextareaField &&
                          displayValue.length > 50 &&
                          resultRows < 3
                        ) {
                          resultRows = 3;
                        } else if (
                          isTextareaField &&
                          displayValue.length > 20 &&
                          resultRows < 2
                        ) {
                          resultRows = 2;
                        }

                        return (
                          <div key={field.id} className="space-y-1 mb-2">
                            <Label
                              htmlFor={`result-${field.id}`}
                              className="text-sm font-medium text-gray-300"
                            >
                              {field.label}
                            </Label>
                            {isTextareaField ? (
                              <Textarea
                                id={`result-${field.id}`}
                                readOnly
                                value={displayValue}
                                className="text-xs text-white bg-gray-700/60 border-gray-600 h-auto min-h-[40px]"
                                rows={resultRows}
                                onClick={(e) =>
                                  (e.target as HTMLTextAreaElement).select()
                                }
                              />
                            ) : (
                              <Input
                                id={`result-${field.id}`}
                                readOnly
                                value={displayValue}
                                className="text-xs text-white bg-gray-700/60 border-gray-600 h-8"
                                onClick={(e) =>
                                  (e.target as HTMLInputElement).select()
                                }
                              />
                            )}
                          </div>
                        );
                      }
                      return null;
                    })
                    .filter(Boolean);

                  if (!sectionHasData) return null;

                  return (
                    <div key={sectionKey}>
                      <h3 className="font-semibold text-base text-[#c8ff00] mb-2 border-b border-gray-600 pb-1">
                        {sectionKey
                          .replace("_", " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </h3>
                      <div
                        className={`grid ${sectionKey === "appearance" ? "grid-cols-2" : "grid-cols-1"} gap-x-4 gap-y-1 pl-2`}
                      >
                        {sectionRenderedFields}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
            <DialogFooter className="mt-6 justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => setView("select")}
                className="text-gray-300"
              >
                Modify Selection
              </Button>
              <div className="flex gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={triggerGeneration}
                        disabled={mutation.isPending || loading}
                      >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Regenerate
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Not quite right? Generate new content for the same
                        fields.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Button
                  type="button"
                  onClick={handleSaveChanges}
                  disabled={mutation.isPending || loading}
                  className="bg-[#c8ff00] text-black hover:bg-[#a8df00] font-bold px-6"
                >
                  {loading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
